/**
 * Cache utility for business listings
 * Uses localStorage with expiration and versioning
 */

const CACHE_PREFIX = 'bizlistings_';
const CACHE_VERSION = 'v1';
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const TOTAL_COUNT_CACHE_KEY = 'bizlistings_totalcount';
const TOTAL_COUNT_CACHE_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes (count changes less frequently)
/** localStorage is typically 5–10MB per origin; cap payload to avoid QuotaExceededError */
const MAX_CACHE_PAYLOAD_BYTES = 2 * 1024 * 1024; // 2MB

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
}

/**
 * Generate cache key from filters and options
 */
function generateCacheKey(filters: any, sort: any, pageSize: number): string {
  const key = JSON.stringify({ filters, sort, pageSize });
  return `${CACHE_PREFIX}${CACHE_VERSION}_${btoa(key).replace(/[^a-zA-Z0-9]/g, '')}`;
}

/**
 * Get cached data if valid
 */
export function getCachedListings<T>(
  filters: any,
  sort: any,
  pageSize: number
): T | null {
  try {
    const cacheKey = generateCacheKey(filters, sort, pageSize);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const entry: CacheEntry<T> = JSON.parse(cached);
    
    // Check version
    if (entry.version !== CACHE_VERSION) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    // Check expiry
    const age = Date.now() - entry.timestamp;
    if (age > CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return entry.data;
  } catch (error) {
    console.warn('Error reading cache:', error);
    return null;
  }
}

/**
 * Set cached data
 */
export function setCachedListings<T>(
  filters: any,
  sort: any,
  pageSize: number,
  data: T
): void {
  const cacheKey = generateCacheKey(filters, sort, pageSize);
  const entry: CacheEntry<T> = {
    data,
    timestamp: Date.now(),
    version: CACHE_VERSION
  };
  
  try {
    const serialized = JSON.stringify(entry);
    if (new Blob([serialized]).size > MAX_CACHE_PAYLOAD_BYTES) {
      return; // Skip caching large payloads to avoid QuotaExceededError
    }
    localStorage.setItem(cacheKey, serialized);
    cleanupOldCache();
  } catch (error) {
    console.warn('Error writing cache:', error);
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      clearAllCache();
      try {
        const serialized = JSON.stringify(entry);
        if (new Blob([serialized]).size <= MAX_CACHE_PAYLOAD_BYTES) {
          localStorage.setItem(cacheKey, serialized);
        }
      } catch (retryError) {
        console.warn('Failed to cache after cleanup:', retryError);
      }
    }
  }
}

/**
 * Clear all business listing cache
 */
export function clearAllCache(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Error clearing cache:', error);
  }
}

/**
 * Clean up old cache entries (keep only most recent 10)
 */
function cleanupOldCache(): void {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys
      .filter(key => key.startsWith(CACHE_PREFIX))
      .map(key => ({
        key,
        timestamp: (() => {
          try {
            const entry = JSON.parse(localStorage.getItem(key) || '{}');
            return entry.timestamp || 0;
          } catch {
            return 0;
          }
        })()
      }))
      .sort((a, b) => b.timestamp - a.timestamp); // Newest first
    
    // Remove entries beyond the 10 most recent
    if (cacheKeys.length > 10) {
      cacheKeys.slice(10).forEach(({ key }) => {
        localStorage.removeItem(key);
      });
    }
  } catch (error) {
    console.warn('Error cleaning up cache:', error);
  }
}

/**
 * Invalidate cache for specific filters
 */
export function invalidateCache(filters?: any): void {
  if (!filters) {
    clearAllCache();
    return;
  }
  
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const entry = JSON.parse(localStorage.getItem(key) || '{}');
          // Simple check: if filters match, remove
          const entryFilters = entry.data?.filters;
          if (entryFilters && JSON.stringify(entryFilters) === JSON.stringify(filters)) {
            localStorage.removeItem(key);
          }
        } catch {
          // Invalid entry, remove it
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.warn('Error invalidating cache:', error);
  }
}

/**
 * Get cached total count
 */
export function getCachedTotalCount(): number | null {
  try {
    const cached = localStorage.getItem(TOTAL_COUNT_CACHE_KEY);
    if (!cached) return null;
    
    const entry: CacheEntry<number> = JSON.parse(cached);
    
    // Check version
    if (entry.version !== CACHE_VERSION) {
      localStorage.removeItem(TOTAL_COUNT_CACHE_KEY);
      return null;
    }
    
    // Check expiry
    const age = Date.now() - entry.timestamp;
    if (age > TOTAL_COUNT_CACHE_EXPIRY_MS) {
      localStorage.removeItem(TOTAL_COUNT_CACHE_KEY);
      return null;
    }
    
    return entry.data;
  } catch (error) {
    console.warn('Error reading total count cache:', error);
    return null;
  }
}

/**
 * Set cached total count
 */
export function setCachedTotalCount(count: number): void {
  try {
    const entry: CacheEntry<number> = {
      data: count,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    
    localStorage.setItem(TOTAL_COUNT_CACHE_KEY, JSON.stringify(entry));
  } catch (error) {
    console.warn('Error writing total count cache:', error);
  }
}
